// Settings.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import DataSync from "../Webflow/DataSync";
import Appearance from "../Webflow/Appearence";
import DefaultQuestions from "../Webflow/DefaultQuestions";
import RecaptchaSettings from "../Webflow/Recaptcha";
import EmailTemplate from "../Webflow/EmailTemplate";
import Users from "../Webflow/Users";
import Script from "../Webflow/Script";
import ExternalMedia from "../Webflow/ExternalVideos";
import Integrations from "../Webflow/Integrations";
import { useDomain } from "context/DomainContext";

import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Drawer,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Collapse,
  LinearProgress,
  Stack,
  Grid,
} from "@mui/material";

import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EmailIcon from "@mui/icons-material/Email";
import SecurityIcon from "@mui/icons-material/Security";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import GroupIcon from "@mui/icons-material/Group";
import ExtensionIcon from "@mui/icons-material/Extension";
import CodeIcon from "@mui/icons-material/Code";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BrushIcon from "@mui/icons-material/Brush"; // Icon for Rebranding
import { useSoftUIController } from "context";
import { setMiniSidenav } from "context";
import UserPricing from "../Webflow/UserPricing";
import { Payment } from "@mui/icons-material";
import { useAuth } from "context/AuthContext";
import { FAST_API_BASE_URL } from "constants";
import { useSettings } from "context/SettingsContext";
import WhitelabelBrandingPage from "../Webflow/WebsiteRebranding";

export default function Settings() {
  const { source } = useDomain();
  const [tabIndex, setTabIndex] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav } = controller;
  const { user, isAuthenticated, getCurrentRole, loading, isInitialized } = useAuth();
  const role = getCurrentRole() || sessionStorage.getItem("role");
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;
  const { settings, loading: settingsLoading } = useSettings();

  // Sync status states
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState(true);
  const [pendingPagesCount, setPendingPagesCount] = useState(0);
  const [totalPagesCount, setTotalPagesCount] = useState(0);
  const [pendingMediaCount, setPendingMediaCount] = useState(0);
  const [totalMediaCount, setTotalMediaCount] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  // Tab visibility tracking
  const isTabVisibleRef = useRef(!document.hidden);
  const pollingIntervalRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  useEffect(() => {
    setMiniSidenav(dispatch, !miniSidenav);
  }, []);

  useEffect(() => {
    if (isMobile) setCollapsed(false);
    else if (isTablet) setCollapsed(true);
  }, [isMobile, isTablet]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isNowVisible = !document.hidden;
      const wasVisible = isTabVisibleRef.current;
      
      isTabVisibleRef.current = isNowVisible;

      if (!wasVisible && isNowVisible && isPolling) {
        // console.log('Tab visible again, restarting polling...');
        clearInterval(pollingIntervalRef.current);
        startPolling();
        fetchSyncStatus(false);
      } else if (wasVisible && !isNowVisible) {
        // console.log('Tab hidden, pausing polling...');
        clearInterval(pollingIntervalRef.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPolling]);

  const fetchSyncStatus = async (showLoading = true) => {
    if (!isTabVisibleRef.current && !showLoading) {
      // console.log('Skipping fetch - tab not visible');
      return false;
    }

    if (showLoading) setSyncLoading(true);
    try {
      const response = await fetch(`${FAST_API_BASE_URL}/api/sync-status`, {
        method: "GET",
        headers: {
          "DOMAIN-TOKEN": domainId,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);

        // Count pages
        const posts = data?.result?.result?.posts || [];
        const pendingPages = posts.filter((post) => post.status === "pending").length;
        setTotalPagesCount(posts.length);
        setPendingPagesCount(pendingPages);

        // Count media
        const media = data?.result?.result?.media || {};
        const videos = media?.videos || [];
        const documents = media?.documents || [];
        const allMedia = [...videos, ...documents];
        const pendingMedia = allMedia.filter((item) => item.status === "pending").length;
        setTotalMediaCount(allMedia.length);
        setPendingMediaCount(pendingMedia);

        // Check if we should continue polling
        const hasPending = pendingPages > 0 || pendingMedia > 0;
        return hasPending;
      }
    } catch (error) {
      console.error("Error fetching sync status:", error);
    } finally {
      if (showLoading) setSyncLoading(false);
    }
    return false;
  };

  // Start polling function
  const startPolling = () => {
    // Only start polling if tab is visible
    if (!isTabVisibleRef.current) {
      // console.log('Not starting polling - tab not visible');
      return;
    }

    clearInterval(pollingIntervalRef.current);
    
    pollingIntervalRef.current = setInterval(async () => {
      // Double-check visibility before fetching
      if (!isTabVisibleRef.current) {
        // console.log('Skipping poll - tab not visible');
        return;
      }

      const hasPending = await fetchSyncStatus(false);
      if (!hasPending) {
        // console.log('No pending items, stopping polling');
        setIsPolling(false);
        clearInterval(pollingIntervalRef.current);
      }
    }, 10000); // Poll every 10 seconds
  };

  // Polling effect - runs when isPolling changes
  useEffect(() => {
    if (isPolling) {
      // console.log('Starting polling...');
      startPolling();
    } else {
      // console.log('Stopping polling...');
      clearInterval(pollingIntervalRef.current);
    }

    return () => {
      clearInterval(pollingIntervalRef.current);
    };
  }, [isPolling, domainId]);

  // Fetch sync status on mount
  useEffect(() => {
    if (source && domainId) {
      fetchSyncStatus();
    }
  }, [source, domainId]);

  // Listen for sync events from child components
  useEffect(() => {
    const handleSyncStarted = () => {
      // console.log('Sync started event received');
      setIsPolling(true);
      fetchSyncStatus(false);
    };

    window.addEventListener('syncStarted', handleSyncStarted);
    
    return () => {
      window.removeEventListener('syncStarted', handleSyncStarted);
    };
  }, []);

  // Calculate progress percentages
  const pagesProgress = totalPagesCount > 0 ? ((totalPagesCount - pendingPagesCount) / totalPagesCount) * 100 : 0;
  const mediaProgress = totalMediaCount > 0 ? ((totalMediaCount - pendingMediaCount) / totalMediaCount) * 100 : 0;

  // useEffect(() => {
  //   // console.log("Settings:", settings);
  //   // console.log("is_whitelabelled:", settings?.is_whitelabelled);
  //   // console.log("whitelabel_scope:", settings?.whitelabel_scope);
  //   // console.log("role:", role);
  // }, [settings, role]);

  const filteredTabs = useMemo(() => {
    if (!source) return [];

    let tabs = [
      { label: "Scripts", component: <Script />, icon: <CodeIcon fontSize="small" /> },
      { label: "Appearance", component: <Appearance />, icon: <ColorLensIcon fontSize="small" /> },
      { label: "Email template", component: <EmailTemplate />, icon: <EmailIcon fontSize="small" /> },
      {
        label: "Google Recaptcha",
        component: <RecaptchaSettings />,
        icon: <SecurityIcon fontSize="small" />,
      },
      { label: "Data Sync", component: <DataSync />, icon: <SyncIcon fontSize="small" /> },
      {
        label: "Default Questions",
        component: <DefaultQuestions />,
        icon: <HelpOutlineIcon fontSize="small" />,
      },
      { label: "External Media", component: <ExternalMedia />, icon: <VideoLibraryIcon fontSize="small" /> },
      { label: "Users", component: <Users />, icon: <GroupIcon fontSize="small" /> },
    ];

    // if (settings && settings.is_whitelabelled == true && settings.whitelabel_scope === "websites" && role === "admin") {
    //   tabs.push( { 
    //     label: "Rebranding", 
    //     component: <WhitelabelBrandingPage />, 
    //     icon: <BrushIcon fontSize="small" /> 
    //   });
    // }

    if (source === "wordpress") {
      tabs = tabs.filter((tab) => tab.label !== "Data Sync");
    }
    if (source === "webflow" || source === "wordpress" || source === "") {
      tabs = tabs.filter((tab) => tab.label !== "Scripts");
    }
    if (role === "read&write") {
      tabs = tabs.filter((tab) => tab.label !== "Integrations");
    }
    if (role !== "admin") {
      tabs = tabs.filter((tab) => tab.label !== "Users");
    }

    return tabs;
  }, [source, role, settings]);

  useEffect(() => {
    if (!source) return;

    const savedTab = sessionStorage.getItem("settingsTabIndex");
    if (savedTab !== null && filteredTabs[savedTab]) {
      setTabIndex(Number(savedTab));
    } else {
      if (source === "custom" || source === "wix" || source === "squarespace") {
        const scriptsIndex = filteredTabs.findIndex((tab) => tab.label === "Scripts");
        if (scriptsIndex !== -1) setTabIndex(scriptsIndex);
        else setTabIndex(0);
      } else {
        setTabIndex(0);
      }
    }
  }, [source, filteredTabs]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleTabSelect = (index) => {
    setTabIndex(index);
    sessionStorage.setItem("settingsTabIndex", index);
    if (isMobile) setMobileOpen(false);
  };

  const SidebarContent = ({ isMobileDrawer = false }) => (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        width: "100%",
      }}
    >
      {isMobileDrawer && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Settings
          </Typography>
          <IconButton onClick={handleDrawerToggle} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {!isMobileDrawer && !isMobile && (
        <Box display="flex" justifyContent={collapsed ? "center" : "flex-end"} width="100%" p={1}>
          <IconButton onClick={() => setCollapsed(!collapsed)} size="small">
            {collapsed ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      <List sx={{ flexGrow: 1, width: "100%", px: isMobileDrawer ? 1 : 2 }}>
        {filteredTabs.map((tab, index) => (
          <Tooltip
            title={!isMobileDrawer && collapsed ? tab.label : ""}
            placement="right"
            key={tab.label}
          >
            <ListItemButton
              selected={tabIndex === index}
              onClick={() => handleTabSelect(index)}
              sx={{
                borderRadius: "8px",
                margin: "4px",
                paddingY: 1,
                paddingX: !isMobileDrawer && collapsed ? 1 : 2,
                "&.Mui-selected": {
                  backgroundColor: "#219297",
                  color: "#fff",
                  "& .MuiListItemIcon-root": { color: "#fff" },
                  "&:hover": {
                    backgroundColor: "#39b1b8",
                    color: "#fff",
                    "& .MuiListItemIcon-root": { color: "#fff" },
                  },
                },
                "&:hover": {
                  backgroundColor: "#39b1b8",
                  color: "#fff",
                  "& .MuiListItemIcon-root": { color: "#fff" },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: !isMobileDrawer && collapsed ? "auto" : 38 }}>
                {tab.icon}
              </ListItemIcon>
              {(isMobileDrawer || !collapsed) && (
                <ListItemText
                  primary={tab.label}
                  primaryTypographyProps={{
                    fontSize: isMobileDrawer ? "0.9rem" : "0.8rem",
                    fontWeight: 500,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </Box>
  );

  if (!source || settingsLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: "70vh" }}>
          <CircularProgress
            size={60}
            sx={{
              color: "#219297",
              animationDuration: "800ms",
            }}
          />
        </SoftBox>
      </DashboardLayout>
    );
  }

  // Show banners if either has pending items
  const showBanners = pendingPagesCount > 0 || pendingMediaCount > 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {isMobile && (
        <SoftBox mb={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              padding: 2,
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
              {filteredTabs[tabIndex]?.label || "Settings"}
            </Typography>
            <IconButton onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
          </Box>
        </SoftBox>
      )}

      <SoftBox display="flex" mt={isMobile ? 1 : 2} sx={{ minHeight: "70vh" }}>
        {!isMobile && (
          <Box
            sx={{
              position: "sticky",
              top: 90,
              height: "auto",
              width: collapsed ? 75 : { md: 200, lg: 220 },
              minWidth: collapsed ? 75 : { md: 200, lg: 220 },
              transition: "all 0.3s ease",
              borderRight: "1px solid #e0e0e0",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: collapsed ? "center" : "flex-start",
              mr: 2,
            }}
          >
            <SidebarContent />
          </Box>
        )}

        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{ "& .MuiDrawer-paper": { boxSizing: "border-box", width: 280 } }}
          >
            <SidebarContent isMobileDrawer={true} />
          </Drawer>
        )}

        <SoftBox
          flexGrow={1}
          sx={{ width: isMobile ? "100%" : "auto", overflowY: "auto", maxHeight: "calc(100vh - 120px)" }}
        >
          <Collapse in={showBanners}>
            <Stack spacing={1} mb={2}>
              {pendingPagesCount > 0 && (
                <Box
                  sx={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #219297",
                    borderLeft: "4px solid #219297",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                    boxShadow: "0 1px 4px rgba(33, 146, 151, 0.1)",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "6px",
                        backgroundColor: "#e3fdf9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {pagesProgress === 100 ? (
                        <CheckCircleIcon sx={{ color: "#219297", fontSize: 18 }} />
                      ) : (
                        <SyncIcon sx={{ color: "#219297", fontSize: 18 }} />
                      )}
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1a1a1a", mb: 0.25 }}>
                        Web Pages Sync
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
                        {pendingPagesCount} of {totalPagesCount} pages pending
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 120, mr: 2 }}>
                      <Box sx={{ position: "relative" }}>
                        <Box
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#e3fdf9",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${pagesProgress}%`,
                              backgroundColor: "#219297",
                              borderRadius: 3,
                              transition: "width 0.4s ease",
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#219297",
                            mt: 0.25,
                            textAlign: "right",
                          }}
                        >
                          {Math.round(pagesProgress)}%
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => fetchSyncStatus()}
                      disabled={syncLoading}
                      sx={{
                        backgroundColor: "#f5f5f5",
                        "&:hover": { backgroundColor: "#e0e0e0" },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <RefreshIcon
                        sx={{
                          fontSize: 16,
                          animation: syncLoading || isPolling ? "spin 1s linear infinite" : "none",
                          color: "#219297",
                        }}
                      />
                    </IconButton>
                  </Stack>
                </Box>
              )}

              {pendingMediaCount > 0 && (
                <Box
                  sx={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #219197",
                    borderLeft: "4px solid #219197",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                    boxShadow: "0 1px 4px rgba(33, 145, 151, 0.1)",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "6px",
                        backgroundColor: "#e0fffc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {mediaProgress === 100 ? (
                        <CheckCircleIcon sx={{ color: "#219197", fontSize: 18 }} />
                      ) : (
                        <VideoLibraryIcon sx={{ color: "#219197", fontSize: 18 }} />
                      )}
                    </Box>
                    <Box flex={1} minWidth={0}>
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1a1a1a", mb: 0.25 }}>
                        External Media Sync
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: "#666" }}>
                        {pendingMediaCount} of {totalMediaCount} items pending
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 120, mr: 2 }}>
                      <Box sx={{ position: "relative" }}>
                        <Box
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#e0fffd",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${mediaProgress}%`,
                              backgroundColor: "#219197",
                              borderRadius: 3,
                              transition: "width 0.4s ease",
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#219197",
                            mt: 0.25,
                            textAlign: "right",
                          }}
                        >
                          {Math.round(mediaProgress)}%
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => fetchSyncStatus()}
                      disabled={syncLoading}
                      sx={{
                        backgroundColor: "#f5f5f5",
                        "&:hover": { backgroundColor: "#e0e0e0" },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <RefreshIcon
                        sx={{
                          fontSize: 16,
                          animation: syncLoading || isPolling ? "spin 1s linear infinite" : "none",
                          color: "#219197",
                        }}
                      />
                    </IconButton>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Collapse>

          <Box
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              minHeight: "20vh",
              border: "1px solid #e0e0e0",
              p: 0,
            }}
          >
            {filteredTabs[tabIndex]?.component}
          </Box>
        </SoftBox>
      </SoftBox>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </DashboardLayout>
  );
}